import { useNotifications } from '../contexts/NotificationContext';

// Type definitions
export type NotificationType = 'success' | 'error' | 'info' | 'warning';

export interface NotificationOptions {
  title?: string;
  message: string;
  type: NotificationType;
  duration?: number;
}

// Notification permission result
export interface NotificationPermissionResult {
  enabled: boolean;
  reason?: 'permission-denied' | 'not-supported' | 'token-failed' | 'setup-failed';
}

// FCM Configuration
// This is the public VAPID key from Firebase console > Project Settings > Cloud Messaging > Web Push certificates
// Make sure to use the key in the correct format (from the Firebase Console)
const VAPID_KEY = 'BBXH9lJhgGc1PHUp_PUXSHVo7a-tYg3oo-gDBibwwQl7dsI-RtBkuXtE0vkWU3Azuw9KBEoSv1ZmTyBr5Z-cius';
const FCM_TOKEN_KEY = 'fcm_token';
const NOTIFICATION_TOPIC = 'stadsauktion-events'; // Topic for all event notifications

// Guard so the foreground onMessage handler is registered only once per app
// session. isNotificationSupported() can be called repeatedly (component mounts/
// re-renders); without this, each call stacked another handler and foreground
// notifications were shown multiple times.
let foregroundHandlerRegistered = false;

// Helper function to get the messaging instance dynamically
// This avoids direct imports that cause type issues
const getMessagingInstance = async () => {
  try {
    // Dynamically import Firebase app and messaging
    const { app } = await import('../firebase');
    const { getMessaging } = await import('firebase/messaging');
    
    // Use type assertion to fix type incompatibility
    return getMessaging(app as any);
  } catch (error) {
    console.error('Error getting messaging instance:', error);
    throw error;
  }
};

// Check if browser supports notifications
export const isNotificationSupported = async (): Promise<boolean> => {
  // Check for both native notifications and FCM support
  const hasNativeSupport = 'Notification' in window;
  
  try {
    // Check if Firebase messaging is supported in this browser
    const { isSupported } = await import('firebase/messaging');
    const hasFCMSupport = await isSupported();
    
    if (hasFCMSupport && !foregroundHandlerRegistered) {
      try {
        // Get messaging instance
        const messaging = await getMessagingInstance();

        // Set up foreground message handler (once)
        const { onMessage } = await import('firebase/messaging');
        foregroundHandlerRegistered = true;
        onMessage(messaging, (payload) => {
          console.log('Foreground message received:', payload);
          
          // We can't use hooks here, so we'll use a different approach
          try {
            // Show a browser notification if the app is in the background
            if (document.visibilityState !== 'visible') {
              const notificationTitle = payload.notification?.title || 'New Notification';
              const notificationOptions = {
                body: payload.notification?.body || 'You have a new notification',
                icon: '/icons/@sas_icon_192x192.png'
              };
              
              // Create a browser notification
              new Notification(notificationTitle, notificationOptions);
            } else {
              // For in-app notifications, we can dispatch an event that the NotificationContext can listen for
              const event = new CustomEvent('app-notification', { 
                detail: {
                  title: payload.notification?.title || 'New Notification',
                  message: payload.notification?.body || 'You have a new notification',
                  type: 'info',
                  duration: 5000
                }
              });
              window.dispatchEvent(event);
            }
          } catch (err) {
            console.error('Error showing notification:', err);
          }
        });
      } catch (error) {
        console.error('Error initializing Firebase messaging:', error);
        return hasNativeSupport;
      }
    }
    
    return hasNativeSupport && hasFCMSupport;
  } catch (error) {
    console.error('Error checking FCM support:', error);
    return hasNativeSupport;
  }
};

// Check if notifications are enabled
export const isNotificationEnabled = (): boolean => {
  try {
    // Check if Notification API is available
    if (!('Notification' in window)) {
      return false;
    }
    
    // Check if permission is granted
    return Notification.permission === 'granted';
  } catch (error) {
    console.error('Error checking notification permission:', error);
    return false;
  }
};

// Request permission to show notifications
export const requestNotificationPermission = async (): Promise<NotificationPermissionResult> => {
  try {
    // Check if browser supports notifications
    if (!('Notification' in window)) {
      return { enabled: false, reason: 'not-supported' };
    }

    // Request notification permission
    const permission = await Notification.requestPermission();
    
    if (permission === 'granted') {
      try {
        // Initialize FCM and subscribe to the topic
        await initializeNotifications();
        return { enabled: true };
      } catch (error) {
        console.error('Failed to initialize notifications:', error);
        return { enabled: false, reason: 'setup-failed' };
      }
    } else {
      return { enabled: false, reason: 'permission-denied' };
    }
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return { enabled: false, reason: 'token-failed' };
  }
};

// Register service worker if needed
const registerServiceWorker = async (): Promise<ServiceWorkerRegistration> => {
  try {
    // First, check if we already have a service worker registration
    if ('serviceWorker' in navigator) {
      const existingReg = await navigator.serviceWorker.getRegistration();
      if (existingReg && existingReg.active) {
        console.log('Using existing service worker registration: ', existingReg);
        return existingReg;
      }
      
      // No active registration found, register a new one
      console.log('No active service worker found, registering new one...');
      try {
        const newReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        console.log('New service worker registered: ', newReg);
        
        // Wait for the service worker to be activated if it's not yet
        if (newReg.installing || newReg.waiting) {
          console.log('Waiting for service worker to activate...');
          return new Promise((resolve) => {
            newReg.addEventListener('updatefound', () => {
              const newWorker = newReg.installing;
              if (newWorker) {
                newWorker.addEventListener('statechange', () => {
                  if (newWorker.state === 'activated') {
                    console.log('Service worker activated successfully');
                    resolve(newReg);
                  }
                });
              }
            });
            
            // If it's already installed but waiting, return it after a short timeout
            setTimeout(() => {
              console.log('Timed out waiting for activation, returning registration anyway');
              resolve(newReg);
            }, 3000);
          });
        }
        
        return newReg;
      } catch (registerError) {
        console.error('Error registering new service worker:', registerError);
        throw registerError;
      }
    } else {
      throw new Error('Service worker not supported in this browser');
    }
  } catch (error) {
    console.error('Service worker registration failed:', error);
    throw error;
  }
};

// Initialize notifications with FCM and subscribe to topic
export const initializeNotifications = async (): Promise<void> => {
  // Check if already initialized with a token
  const existingToken = localStorage.getItem(FCM_TOKEN_KEY);
  let currentToken = existingToken;
  
  try {
    console.log('Starting notification initialization...');
    
    // Check permission status
    if (Notification.permission !== 'granted') {
      console.log('Notification permission not granted yet');
      throw new Error('Notification permission not granted');
    }
    
    // Skip service worker for non-browser environments
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      console.log('Service workers not supported or not in browser environment');
      return;
    }
    
    // Register service worker first
    console.log('Registering service worker...');
    let serviceWorkerReg: ServiceWorkerRegistration;
    try {
      serviceWorkerReg = await registerServiceWorker();
    } catch (swError) {
      console.error('Service worker registration failed completely:', swError);
      // Continue without service worker - might still work for foreground notifications
      console.log('Continuing without service worker - push notifications may not work in background');
      serviceWorkerReg = null as any;
    }
    
    // Get messaging instance
    console.log('Getting messaging instance...');
    try {
      const messaging = await getMessagingInstance();
      console.log('Messaging instance obtained');
      
      // If we don't have a token or need to refresh it
      if (!currentToken) {
        console.log('No existing token found, retrieving new token...');
        try {
          // Import token functions
          const { getToken } = await import('firebase/messaging');
          
          // Get FCM token with the VAPID key
          console.log('Requesting FCM token from messaging service...');
          
          const tokenOptions = serviceWorkerReg 
            ? { vapidKey: VAPID_KEY, serviceWorkerRegistration: serviceWorkerReg }
            : { vapidKey: VAPID_KEY };
            
          try {
            currentToken = await getToken(messaging, tokenOptions);
            
            if (currentToken) {
              // Save the token to localStorage
              localStorage.setItem(FCM_TOKEN_KEY, currentToken);
              console.log('FCM token obtained and saved:', currentToken.substring(0, 10) + '...');
            } else {
              console.error('No FCM token received from messaging service');
              throw new Error('Failed to obtain notification token');
            }
          } catch (tokenError) {
            console.error('Error getting FCM token:', tokenError);
            throw tokenError;
          }
        } catch (importError) {
          console.error('Error importing token functions:', importError);
          throw importError;
        }
      } else {
        console.log('Using existing FCM token:', currentToken.substring(0, 10) + '...');
      }
    } catch (messagingError) {
      console.error('Failed to initialize Firebase messaging:', messagingError);
      // Return success anyway - we have notification permission which is most important
      console.log('Notifications will work in foreground mode only');
    }
    
    // Subscribe to the events topic using our Cloud Function
    if (currentToken) {
      console.log('Subscribing to notification topic...');
      try {
        await subscribeToTopic(NOTIFICATION_TOPIC, currentToken);
        console.log('Successfully subscribed to topic');
      } catch (topicError) {
        console.error('Error subscribing to topic:', topicError);
        // Continue execution even if topic subscription fails
      }
    }
    
    console.log('Notification initialization complete');
    return Promise.resolve();
  } catch (error) {
    console.error('Error initializing notifications:', error);
    throw error;
  }
};

// Subscribe to an FCM topic using a Cloud Function
const subscribeToTopic = async (topic: string, token: string): Promise<void> => {
  try {
    // Get if we're in development or production
    const isDevelopment = window.location.hostname === 'localhost' || 
      window.location.hostname === '127.0.0.1';
    
    if (isDevelopment) {
      console.log('Development environment detected, using HTTP endpoint for topic subscription');
      // In development, use direct HTTP endpoint with CORS support
      const { getAuth } = await import('firebase/auth');
      const { app } = await import('../firebase');
      const auth = getAuth(app as any);
      const idToken = await auth.currentUser?.getIdToken();

      const response = await fetch(
        `https://us-central1-stadsauktion-sundsvall-pickup.cloudfunctions.net/subscribeToTopicHttp`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {}),
          },
          body: JSON.stringify({ topic, token })
        }
      );
      
      if (!response.ok) {
        // Try to get error details
        let errorDetails = '';
        try {
          const errorData = await response.json();
          errorDetails = errorData.error || '';
        } catch (e) {
          // Ignore parsing errors
        }
        throw new Error(`Failed to subscribe to topic: ${response.status} ${response.statusText} ${errorDetails}`);
      }
      
      const result = await response.json();
      console.log(`Successfully subscribed to topic: ${topic}`, result);
    } else {
      // In production, use callable function
      // Import Firebase functions
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const { app } = await import('../firebase');
      
      // Get functions instance
      const functions = getFunctions(app as any);
      
      // Call the Cloud Function to subscribe to the topic
      const subscribeToTopicFn = httpsCallable(functions, 'subscribeToTopic');
      
      // Call the function with the topic and token
      const result = await subscribeToTopicFn({ topic, token });
      
      console.log(`Successfully subscribed to topic: ${topic}`, result);
    }
    
    // Mark as subscribed in localStorage
    localStorage.setItem(`topic_${topic}`, 'subscribed');
  } catch (error) {
    console.error(`Error subscribing to topic ${topic}:`, error);
    // Don't throw the error to avoid blocking the entire notification initialization
    // Just log it since topic subscription is optional for basic functionality
  }
};

// Get the current FCM token
export const getFCMToken = (): string | null => {
  return localStorage.getItem(FCM_TOKEN_KEY);
};

// Register FCM token with backend (simplified for topic-based approach)
export const registerDeviceWithBackend = async (userId: string): Promise<boolean> => {
  const token = getFCMToken();
  
  if (!token) {
    console.error('No FCM token available');
    return false;
  }
  
  // For topic-based approach, we don't need to register with backend
  // Just ensure we're subscribed to the topic
  try {
    await subscribeToTopic(NOTIFICATION_TOPIC, token);
    return true;
  } catch (error) {
    console.error('Error subscribing to topic:', error);
    return false;
  }
};

// Hook to get notification functions
export const useNotificationService = () => {
  const { addNotification } = useNotifications();

  // Helper functions for each notification type
  const success = (message: string, title?: string, duration?: number) => {
    addNotification({
      type: 'success',
      message,
      title,
      duration
    });
  };

  const error = (message: string, title?: string, duration?: number) => {
    addNotification({
      type: 'error',
      message,
      title,
      duration
    });
  };

  const info = (message: string, title?: string, duration?: number) => {
    addNotification({
      type: 'info',
      message,
      title,
      duration
    });
  };

  const warning = (message: string, title?: string, duration?: number) => {
    addNotification({
      type: 'warning',
      message,
      title,
      duration
    });
  };

  const notify = (options: NotificationOptions) => {
    addNotification(options);
  };

  return {
    success,
    error,
    info,
    warning,
    notify
  };
};

// Example of how to use it in a component:
/*
  const MyComponent = () => {
    const { success, error } = useNotificationService();
    
    const handleSave = async () => {
      try {
        // Save operation
        success('Data saved successfully!', 'Save Complete');
      } catch (err) {
        error('Failed to save data', 'Error');
      }
    };
    
    return <button onClick={handleSave}>Save</button>;
  };
*/ 